// services/sixCatalog/SixCatalogServiceImpl.ts

import { SixCatalogService } from '@/services/abstract/SixCatalogService';
import type { FetcherService } from '@/services/abstract/FetcherService';
import type { FetchSixesCatalogParams, SixesCatalogPayloadDTO, SixFavoriteDTO } from '@/types/SixCatalogDTOs';
import { sixEndpoints } from '@/api/endpoints';
import { buildQueryString } from '@/utils/buildQuery';

/**
 * Implementation of Six catalog service for browsing and searching
 * available Six workspaces.
 *
 * Provides functionality for:
 * - Fetching all Sixes
 * - Searching Sixes by name or description
 * - Filtering by mySixes, recent, favorites
 *
 * @see SixCatalogService
 */
export class SixCatalogServiceImpl extends SixCatalogService {
  /**
   * Creates an instance of SixCatalogServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  /**
   * Retrieves a list of Six workspaces with optional search, filters, sorting, and pagination.
   *
   * @endpoint GET /private/v2/sixes
   * @param params - Optional search, filter, sort, and pagination parameters
   * @returns Catalog payload with sixes and pagination
   * @throws UnauthorizedError if user is not authenticated
   */
  async fetchSixes(params?: FetchSixesCatalogParams): Promise<SixesCatalogPayloadDTO> {
    const queryString = buildQueryString({
      search: params?.search,
      mySixes: params?.filters?.includes('mySixes') === true ? true : undefined,
      favorites: params?.filters?.includes('favorites') === true ? true : undefined,
      recent: params?.filters?.includes('recent') === true ? true : undefined,
      sortField: params?.sortField,
      sortDirection: params?.sortDirection,
      page: params?.page,
      pageSize: params?.pageSize,
    });

    const url = `${sixEndpoints.getSixes}${queryString}`;
    return await this.fetcher.get<SixesCatalogPayloadDTO>(url);
  }

  async deleteSix(id: string): Promise<void> {
    await this.fetcher.del(sixEndpoints.deleteSix(id));
  }

  async toggleFavoriteSix(id: string): Promise<SixFavoriteDTO> {
    return await this.fetcher.post(sixEndpoints.toggleFavoriteSix(id), {});
  }
}
